import Clarity from '@microsoft/clarity';
import cn from 'classnames';
import React, { type JSX } from 'react';
import {
  MessageSquare as CustomerServiceIcon,
  User as UserIcon,
  Menu,
  X as CloseIcon,
} from 'lucide-react';
import { Link, NavLink, useLocation, useRouteMatch } from 'react-router-dom';
import type { User } from '@/App.interfaces';
import { PurchaseCTA } from '@/components/PurchaseCTA';
import { useApp } from '@/helpers/api-helpers';
import useCourseNavToggle from '@/hooks/use-course-nav-toggle';
import logo from '@/icons/logo-inline.svg';
import HomeIcon from '@/icons/home.svg?react';
import EnrollmentsIcon from '@/icons/profile-enrollments.svg?react';
import InfoIcon from '@/icons/profile-info.svg?react';
import BillingIcon from '@/icons/profile-billing.svg?react';
import ProfileToggleIcon from '@/icons/profile-mobile-toggle.svg?react';
import SignOutIcon from '@/icons/sign-out.svg?react';
import styles from './MobileAppHeader.module.css';

function CourseNav() {
  const { isNavOpen, toggleNav } = useCourseNavToggle();
  return (
    <button
      id={styles.navButton}
      aria-label="Toggle Navigation Menu"
      aria-expanded={isNavOpen}
      data-testid="nav-button"
      onClick={toggleNav}
    >
      <Menu />
    </button>
  );
}

function AppMenu() {
  const { user, tabs } = useApp() as {
    user: User;
    tabs: { path: string; title: string; icon: JSX.Element }[];
  };
  const profilePathMatch = useRouteMatch('/profile');
  const [open, setOpen] = React.useState(false);
  const [shouldShowProfileMenu, setShouldShowProfileMenu] =
    React.useState(false);

  const toggle = React.useCallback(() => {
    setOpen((open) => !open);
    setShouldShowProfileMenu(false);
  }, []);

  const close = React.useCallback(() => {
    setOpen(false);
    setShouldShowProfileMenu(false);
  }, []);

  function showProfileMenu() {
    setShouldShowProfileMenu(true);
  }

  function hideProfileMenu() {
    setShouldShowProfileMenu(false);
  }

  function handleCustomerServiceClick() {
    Clarity.setTag('customer-service', 'clicked');
  }

  React.useEffect(() => {
    close();
  }, [close]);

  return user ? (
    <div className={styles.navWrapper}>
      <div className={styles.navContent}>
        <button
          aria-label="App Menu"
          data-testid="app-menu"
          type="button"
          onClick={toggle}
        >
          {open ? <CloseIcon /> : <UserIcon />}
        </button>
        {open && (
          <nav>
            {shouldShowProfileMenu ? (
              <ul>
                <li className={styles.open}>
                  <div onClick={hideProfileMenu} onKeyUp={hideProfileMenu}>
                    <HomeIcon />
                    <span>{user.firstName}</span>
                    <ProfileToggleIcon style={{ transform: 'rotate(90deg)' }} />
                  </div>
                </li>
                <li>
                  <NavLink
                    to="/profile/enrollments"
                    onClick={close}
                    onKeyUp={close}
                  >
                    <EnrollmentsIcon />
                    <span>Enrollments</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/profile/info" onClick={close} onKeyUp={close}>
                    <InfoIcon />
                    <span>Info</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/profile/billing"
                    onClick={close}
                    onKeyUp={close}
                  >
                    <BillingIcon />
                    <span>Billing</span>
                  </NavLink>
                </li>
              </ul>
            ) : (
              <ul>
                {tabs?.map(({ path, title, icon }) => (
                  <li key={path}>
                    <NavLink to={path} onClick={close} onKeyUp={close}>
                      {icon}
                      <span>{title}</span>
                    </NavLink>
                  </li>
                ))}
                <li>
                  <div
                    className={(profilePathMatch && 'active') || undefined}
                    onClick={showProfileMenu}
                    onKeyUp={showProfileMenu}
                  >
                    <HomeIcon />
                    <span>{user.firstName}</span>
                    <ProfileToggleIcon />
                  </div>
                </li>
                <li>
                  <a
                    rel="noreferrer"
                    target="_blank"
                    href="https://recademics.com/customer-support/"
                    onClick={handleCustomerServiceClick}
                  >
                    <CustomerServiceIcon />
                    <span>Customer Service</span>
                  </a>
                </li>
                <li>
                  <NavLink to="/logout" onClick={close} onKeyUp={close}>
                    <SignOutIcon />
                    <span>Sign Out</span>
                  </NavLink>
                </li>
              </ul>
            )}
          </nav>
        )}
      </div>
    </div>
  ) : null;
}

function AppLogo() {
  const { branding } = useApp() as {
    branding: {
      id: string | null;
      logoUrl: string | null;
      name: string;
    } | null;
  };
  return (
    <Link className={styles.logo} to="/">
      <img
        className={branding?.logoUrl ? styles.brandedLogo : undefined}
        src={branding?.logoUrl ?? (logo as unknown as string)}
        alt={branding?.name ?? 'Recademics'}
      />
      {branding?.id && (
        <span className={styles.poweredBy}>Powered by Recademics</span>
      )}
    </Link>
  );
}

export function MobileAppHeader({ className }: { className?: string }) {
  const location = useLocation();
  const { authState } = useApp() as { authState: string };
  const wrapperClassName = cn(styles.wrapper, className);

  return (
    <>
      <header className={wrapperClassName}>
        {location.pathname.startsWith('/courses/') &&
          !location.pathname.includes('dashboard') && <CourseNav />}
        <AppLogo />
        <AppMenu />
      </header>
      {authState === 'signIn' && <PurchaseCTA className={styles.purchaseCTA} />}
    </>
  );
}
